/**
 * Одна дата среза для экранов: «на какое число смотрим».
 *
 * Контекст анализа называет дату, только когда человек выбрал её сам. Без
 * выбора срез «как знаем сейчас», и «сегодня» в нём — число СЕРВЕРА (`horizon`
 * ответа). Часы браузера тут не годятся: у сервера и у вкладки разные
 * часовые пояса и разные полночи, и экраны одного кабинета говорили бы «срез
 * 11 окт» и «на срезе 12 окт». Так же устроены экраны хокима
 * (`ctx.asOf ?? ответ.horizon`).
 *
 * Дата браузера остаётся только значением по умолчанию в поле ввода («дата
 * выполнения») и всегда зажата датой среза (`clampToSlice`).
 */

import { useEffect, useState } from 'react';
import { knownServerToday, serverToday } from '../api/serverToday';
import { localIsoDate } from '../lib/localDate';
import { useAnalysisContext } from './context';

/**
 * «Сегодня» сервера. Нужно там, где срез сравнивается с сегодняшним днём
 * («смотрим прошлое»), и там, где срез контекстом не назван. Пока сервер не
 * ответил — `null`.
 */
export function useServerToday(enabled = true): string | null {
  const [today, setToday] = useState<string | null>(() => knownServerToday());
  useEffect(() => {
    if (today || !enabled) return undefined;
    let cancelled = false;
    void serverToday().then((value) => {
      if (!cancelled) setToday(value);
    });
    return () => {
      cancelled = true;
    };
  }, [today, enabled]);
  return today;
}

/**
 * Дата среза экрана: названная контекстом, иначе «сегодня» сервера. Пока
 * сервер не ответил — `null`: показывать нечего, подставлять браузерную дату
 * нельзя (мелькнёт чужое число).
 */
export function useSliceDate(): string | null {
  const { asOf } = useAnalysisContext();
  // Срез назван контекстом — сервер о «сегодня» не спрашиваем.
  const today = useServerToday(!asOf);
  return asOf ?? today;
}

/**
 * Дата по умолчанию для поля ввода («когда сделана работа»): сегодня по часам
 * человека, но не позже среза — иначе поле предложило бы дату, которую форма
 * же и отвергнет. Срез ещё неизвестен — `null`.
 */
export function clampToSlice(slice: string | null, browserToday: string = localIsoDate()): string | null {
  if (!slice) return null;
  return browserToday <= slice ? browserToday : slice;
}
